import React from 'react'

import Bottlenecks from '../helper/bottlenecks'
import LargeNumber from '../helper/large-number'
import Memory from '../helper/memory'
import Milliseconds from '../helper/milliseconds'
import Percent from '../helper/percent'
import Shorten from '../helper/shorten'

import { Bottleneck } from '../domain/Bottleneck'
import { Operation } from '../domain/Operation'
import { isAggregatedTrace, isCallgraph, TraceSummarySingleView } from '../domain/TraceSummary'
import Link from '../helper/link'
import Loading from '../helper/loading'
import MomentDate from '../momentDate'
import { TraceContextLinks } from '../context-links/TraceContextLinks'

interface Props {
    profile: TraceSummarySingleView | null
    operation?: Operation
    prefix: string
    route: string
    bottlenecks?: Bottleneck
    bottleneckTypes?: string[]
    compact: boolean
    rowType: string
    isSharedTraceView: boolean
}

class Overview extends React.Component<Props> {
    public static defaultProps = {
        route: 'XhprofProfilerBundle.Profile.quick',
        rowType: 'odd',
        prefix: '',
        compact: false,
        isSharedTraceView: false,
    }

    public render() {
        if (!this.props.profile) {
            return <Loading />
        }

        let statusCode = ''
        if (this.props.profile.statusCode) {
            if (this.props.profile.statusCode < 300) {
                statusCode = 'success'
            } else if (this.props.profile.statusCode < 400) {
                statusCode = 'warning'
            } else {
                statusCode = 'danger'
            }
        }

        // this is really ugly right now: profile is the binary profile.spans usually is the raw span format from the binary profile
        // except, when it is an aggregated profile: in that case it is the output of TraceParser::parse()
        // we could try to use the proper `this.props.traces[0].spans` from trace.tsx, but that would mean more changes, just to get this data into overview.tsx
        const [rootSpan, ...spans] = this.props.profile?.spans?.spans || []

        let server: string | null = this.props.profile.server ? this.props.profile.server.name : 'unknown server'
        if (isAggregatedTrace(this.props.profile) && rootSpan?.annotations?.trace_aggregation_report?.servers.length > 0) {
            server = null
        }

        return (
            <table className="table table-condensed profile-list">
                <tbody>
                    <tr className={this.props.rowType + ' summary'}>
                        <td colSpan={1} className="number short">
                            <Milliseconds value={this.props.profile.wallTime} />
                        </td>
                        <td colSpan={8} className="operation">
                            {this.props.prefix ? <span className="tw-font-weight-strong tw-mr-4">{this.props.prefix}</span> : null}
                            <Link className="profile-list-item" route={this.props.route} params={{ profile: this.props.profile.id }}>
                                <strong>
                                    {this.props.bottlenecks ? (
                                        <Bottlenecks
                                            bottlenecks={this.props.bottlenecks}
                                            bottleneckTypes={this.props.bottleneckTypes || []}
                                        />
                                    ) : null}
                                    <Shorten
                                        string={
                                            (this.props.profile.name ? this.props.profile.name + ' | ' : '') +
                                                (this.props.operation ? this.props.operation.identifier : '') || 'Trace Details'
                                        }
                                    />
                                </strong>
                                {this.props.operation && this.props.operation.identifier !== this.props.operation.name ? (
                                    <small className={'text-muted'}> {this.props.operation.name}</small>
                                ) : null}
                            </Link>
                            {this.props.profile.errorType && (
                                <span>
                                    {' '}
                                    <small>
                                        {this.props.profile.isIgnoredErrorType ? (
                                            <span
                                                className="text-muted tw-tooltip tw-tooltip-dataText tw-tooltip--bottom"
                                                data-tooltip-text="This exception class is classified as ignored in this project, probably because its used for control-flow instead of actually being an error."
                                            >
                                                {this.props.profile.errorType}
                                            </span>
                                        ) : (
                                            <span className="text-danger">{this.props.profile.errorType}</span>
                                        )}
                                    </small>
                                </span>
                            )}
                            {this.props.isSharedTraceView ? null : (
                                <span>
                                    {' '}
                                    <Link
                                        className="btn btn-default btn-small"
                                        route="XhprofProfilerBundle.Profile.TraceMetadata"
                                        params={{ profile: this.props.profile.id }}
                                    >
                                        <small className=" small-margin"> Edit</small>
                                    </Link>
                                </span>
                            )}
                            {this.props.profile.transactionTags.map(tag => (
                                <span
                                    key={tag}
                                    className="tw-ml-8 label label-outline-default tw-tooltip tw-tooltip-dataText tw-tooltip--right"
                                    data-tooltip-text={`This trace is tagged as ${tag}.`}
                                >
                                    {tag}
                                </span>
                            ))}
                            <div className="pull-right">{this.renderMeasureDate(rootSpan)}</div>
                        </td>
                        {this.props.isSharedTraceView ? null : (
                            <td className="number">
                                <div className="pull-right">
                                    <TraceContextLinks trace={this.props.profile} />
                                </div>
                            </td>
                        )}
                    </tr>
                    <tr className={this.props.rowType + ' details'}>
                        <td colSpan={1} className="number short">
                            <Memory value={(this.props.profile.memory || 0) * 1024} />
                        </td>
                        <td className="title word" colSpan={6}>
                            <div className="profile-list-item-metadata">
                                {statusCode && (
                                    <span>
                                        <span className={'label label-' + statusCode}>{this.props.profile.statusCode}</span>{' '}
                                    </span>
                                )}
                                <small>
                                    <Shorten string={this.props.profile.title || 'unknown'} length={250} />
                                </small>
                            </div>
                        </td>
                        {server ? (
                            <td className="number word text-ellipsis" title={server}>
                                <small className="hidden-xs hidden-sm hidden-md">
                                    <Shorten length={20} string={server} />
                                </small>
                            </td>
                        ) : (
                            <td>&nbsp;</td>
                        )}
                        <td className="number word">
                            <small className="hidden-xs hidden-sm hidden-md">
                                {this.props.profile.service} @ {this.props.profile.environment}
                            </small>
                        </td>
                        <td>&nbsp;</td>
                    </tr>
                    <tr className={this.props.rowType + ' details layers'}>
                        <td className="number short">
                            {isCallgraph(this.props.profile) ? (
                                <i className="fa fa-sitemap tw-color-grey-600" title="callgraph trace" />
                            ) : null}
                            {isAggregatedTrace(this.props.profile) ? (
                                <i className="tw-ml-4 fa fa-tasks tw-color-grey-600" title={this.props.profile.title + ''} />
                            ) : null}
                        </td>
                        <td className="number">
                            {this.props.profile.layers.db ? (
                                <span>
                                    <small>SQL ({<LargeNumber value={this.props.profile.layers.db.ct} class="default" />}):</small>{' '}
                                    <Milliseconds value={this.props.profile.layers.db.wt} class="layer-sql" />
                                </span>
                            ) : null}
                        </td>
                        <td className="number">
                            {this.props.profile.layers.http ? (
                                <span>
                                    <small>HTTP ({<LargeNumber value={this.props.profile.layers.http.ct} class="default" />}):</small>{' '}
                                    <Milliseconds value={this.props.profile.layers.http.wt} class="layer-http" />
                                </span>
                            ) : null}
                        </td>
                        <td className="number">
                            {this.props.profile.layers.cache ? (
                                <span>
                                    <small>Memcache ({<LargeNumber value={this.props.profile.layers.cache.ct} class="default" />}):</small>{' '}
                                    <Milliseconds value={this.props.profile.layers.cache.wt} class="layer-cache" />
                                </span>
                            ) : null}
                        </td>
                        <td className="number">
                            {this.props.profile.layers.apcu ? (
                                <span>
                                    <small>APCu</small> <Milliseconds value={this.props.profile.layers.apcu.wt} class="layer-cache" />
                                </span>
                            ) : null}
                        </td>
                        <td className="number">
                            {this.props.profile.layers.redis ? (
                                <span>
                                    <small>Redis ({<LargeNumber value={this.props.profile.layers.redis.ct} class="default" />}):</small>{' '}
                                    <Milliseconds value={this.props.profile.layers.redis.wt} class="layer-redis" />
                                </span>
                            ) : null}
                        </td>
                        <td className="number">
                            {this.props.profile.layers.mongo ? (
                                <span>
                                    <small>MongoDB ({<LargeNumber value={this.props.profile.layers.mongo.ct} class="default" />}):</small>{' '}
                                    <Milliseconds value={this.props.profile.layers.mongo.wt} class="layer-mongo" />
                                </span>
                            ) : null}
                        </td>
                        <td className="number">
                            {this.props.profile.layers.al ? (
                                <span>
                                    <small>Autoloading</small> <Milliseconds value={this.props.profile.layers.al.wt} class="layer-io" />
                                </span>
                            ) : null}
                        </td>
                        <td className="number">
                            {this.props.profile.layers.fio ? (
                                <span>
                                    <small>File I/O</small> <Milliseconds value={this.props.profile.layers.fio.wt} class="layer-io" />
                                </span>
                            ) : null}
                        </td>
                        <td className="number">
                            {this.props.profile.layers.runq ? (
                                <span>
                                    <small>Waiting for CPU</small>{' '}
                                    <Milliseconds value={this.props.profile.layers.runq.wt} class="layer-runq" />
                                </span>
                            ) : null}

                            {!this.props.profile.layers.runq && this.props.profile.layers.uio ? (
                                <span>
                                    <small>Unaccounted Wait</small>{' '}
                                    <Milliseconds value={this.props.profile.layers.uio.wt} class="layer-io" />
                                </span>
                            ) : this.props.profile.layers.io ? (
                                <span>
                                    <small>
                                        Non-CPU time (<Percent value={(this.props.profile.layers.io.perc || 0) / 100} />
                                        ):
                                    </small>
                                    <Milliseconds value={this.props.profile.layers.io.wt} class="layer-io" />
                                </span>
                            ) : null}
                        </td>
                    </tr>
                </tbody>
            </table>
        )
    }

    private renderMeasureDate(rootSpan: any) {
        if (!this.props.profile) {
            return null
        }

        if (rootSpan?.annotations?.trace_aggregation_report?.min_date) {
            return (
                <span style={{ whiteSpace: 'nowrap' }}>
                    From <MomentDate date={rootSpan.annotations.trace_aggregation_report.min_date} /> to{' '}
                    <MomentDate date={rootSpan.annotations.trace_aggregation_report.max_date} />
                </span>
            )
        }

        return <MomentDate date={this.props.profile.measureDate.date} />
    }
}

export default Overview
